import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Checkbox } from "../../../components/AntComponents";
import Icon from "../../../components/AppIcon";
import { loginWithEmail } from "../../../api/authService";
import { ApiError } from "../../../api/clientService";
import { mediamtxService } from "@/mediaservices/mediamtxService";
import { mediasoupService } from "@/mediaservices/mediasoupService";

const USE_MEDIAMTX = String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === "true";

type Errors = {
  email?: string;
  password?: string;
  general?: string;
};

const AUTH_REMEMBER_KEY = "auth.remember";

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isConnectingWebSocket, setIsConnectingWebSocket] = useState(false);

  const validateForm = () => {
    const newErrors: Errors = {};

    if (!formData.email) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name as keyof Errors]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors((prev) => ({ ...prev, general: "" }));

    try {
      // ✅ STEP 1: Login API call first
      await loginWithEmail({
        email: formData.email,
        password: formData.password,
      });

      setIsConnectingWebSocket(true);
      
      try {
        if (USE_MEDIAMTX) {
          // ✅ Check if already connected first
          if (mediamtxService.isConnected()) {
            console.log('MediaMTX already connected');
          } else {
            console.log('🔌 Initializing MediaMTX connection...');
            await Promise.race([
              mediamtxService.initialize(),
              new Promise((resolve) => {
                const checkAuth = setInterval(() => {
                  if (mediamtxService.isAuthenticated) {
                    clearInterval(checkAuth);
                    console.log('MediaMTX authenticated - proceeding immediately');
                    resolve(true);
                  }
                }, 50);
                setTimeout(() => {
                  clearInterval(checkAuth);
                  resolve(true);
                }, 3000);
              })
            ]);
          }
        } else {
          if (mediasoupService.isConnected?.()) {
            console.log('✅ MediaSoup already connected');
          } else {
            // ✅ FASTER: Add timeout for MediaSoup too
            await Promise.race([
              mediasoupService.initialize(),
              new Promise((resolve) => 
                setTimeout(() => {
                  console.log('⚠️ MediaSoup timeout - proceeding anyway');
                  resolve(true);
                }, 3000)
              )
            ]);
            console.log('✅ MediaSoup connection successful');
          }
        }
        
        // ✅ STEP 3: Store remember preference
        localStorage.setItem(AUTH_REMEMBER_KEY, String(!!formData.rememberMe));
        
        // ✅ STEP 4: Navigate after successful connection (or timeout)
        navigate("/stream-management");
        
      } catch (wsError: any) {
        if (wsError?.message === 'Connection timeout') {
          console.log('⚠️ WebSocket connection timed out, but proceeding with login...');
          localStorage.setItem(AUTH_REMEMBER_KEY, String(!!formData.rememberMe));
          navigate("/stream-management");
          return;
        }
        if (wsError?.message?.includes("Operator is already logged in")) {
          setErrors((prev) => ({ 
            ...prev,
          }));
        } else {
          console.warn('WebSocket connection failed, but continuing with login...');
          localStorage.setItem(AUTH_REMEMBER_KEY, String(!!formData.rememberMe));
          navigate("/stream-management");
        }
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const msg =
          err.envelope?.message ||
          err.envelope?.error ||
          "Login failed. Please check your credentials.";
        setErrors((prev) => ({ ...prev, general: msg }));
      } else {
        setErrors((prev) => ({
          ...prev,
          general: "Login failed. Please check your connection and try again.",
        }));
      }
    } finally {
      setIsLoading(false);
      setIsConnectingWebSocket(false);
    }
  };
  const buttonText = isConnectingWebSocket 
    ? "Connecting..." 
    : isLoading 
    ? "Signing In..." 
    : "Sign In";

  const isSubmitting = isLoading || isConnectingWebSocket;

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email */}
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-foreground">
            Email Address
          </label>
          <Input
            id="email"
            type="email"
            name="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            required
            disabled={isLoading}
            className="w-full"
          />
        </div>

        {/* Password */}
        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-foreground">
            Password
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleInputChange}
              error={errors.password}
              required
              disabled={isLoading}
              className="w-full pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-micro"
              disabled={isLoading}
            >
              <Icon name={showPassword ? "Eye" : "EyeOff"} size={18} />
            </button>
          </div>
        </div>

        {/* Error */}
        {errors.general && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-700">{errors.general}</span>
            </div>
          </div>
        )}

        {/* Submit */}
        <Button 
          type="primary"
          htmlType="submit" 
          variant="default" 
          size="lg" 
          className="w-full"
          loading={isSubmitting}
          fullWidth
          iconName="LogIn"
          iconPosition="left"
        >
          {buttonText}
        </Button>
      </form>
    </div>
  );
};

export default LoginForm;
